import React, { useEffect, useMemo } from 'react';
import { Grid, Typography, useMediaQuery, useTheme, Box, Chip, Paper, alpha } from '@mui/material';
import SingleCSP, { sortCSPs } from './SingleCSP';
import { ChargeableWeight, TableHeader } from './styles';
import Actions from './Actions';
import SingleCSPMobile from './SingleCSPMobile';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '../../../store';
import { HandleBookCSPType } from '../../../store/reducers/GetRates/Type';
import { getWarehouseListForShipment } from '@Reducers/Warehouse';
import { getSpecificWarehouseListForShipment } from '@Reducers/Warehouse/actions';
import { userLogin } from '@Services/hasAdminAccess';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType } from '../../../types/shipment';
import { isExportShipment } from '../../../utils/exportShipment';
import { isEUCountry } from '../../../utils/euCompliance';
import { useEurExchangeRate } from '../../../utils/currencyConversion';
import { getDutyCharge } from '../../../utils/pricing';
import SbButton from '../../../components/Button';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface PropsType {
  weightUnit: string;
  setShowRates?: (show: boolean) => void;
  handleBookCSP: HandleBookCSPType;
}

function CSPList({ /*weightUnit,*/ setShowRates, handleBookCSP }: PropsType) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const CSPChargesData = useSelector((state: RootStateType) => state?.getRates?.CSPChargesData);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const orgCountryObj = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const orgCountryName = orgCountryObj?._country?.name || orgCountryObj?.currency?.name || '';
  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup?.organizationData?.currency?.name || '',
  );
  const { name = '' } = organizationData || {};
  const accountNumber = useSelector((state: RootStateType) => state?.loginUser?.profile?.accountNumber) || '';
  const isUserLogin = userLogin();
  const { CreateShipment } = useSelector((state: RootStateType) => state.accountLookup);

  const shipmentContext = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const shipmentType = shipmentContext?.shipmentType;
  const setShipmentType = shipmentContext?.setShipmentType;
  const totalShipmentValue = shipmentContext?.totalShipmentValue || 0;
  const extraDetails = shipmentContext?.extraDetails;
  const shipper = shipmentContext?.shipper;
  const consignee = shipmentContext?.consignee;
  const selectedCSP = shipmentContext?.selectedCSP;

  useEffect(() => {
    if (name === 'Cargo Solutions Network') {
      dispatch(getWarehouseListForShipment());
      dispatch(
        getSpecificWarehouseListForShipment(
          isUserLogin
            ? accountNumber
            : CreateShipment?.selectedUser?.accountNumber || CreateShipment?.selectedUser?._accountId?.accountNumber,
        ),
      );
    }
  }, [dispatch]);

  const sortedCharges = useMemo(() => {
    return sortCSPs(CSPChargesData?.charges || []);
  }, [CSPChargesData?.charges]);

  // Export shipment detection
  const isExport = useMemo(() => {
    return isExportShipment({
      orgCountryName,
      shipper,
      consignee,
    });
  }, [orgCountryName, shipper, consignee]);

  const currentDutyType = useMemo(() => {
    const raw =
      typeof shipmentType?.dutyCharge === 'object' ? shipmentType?.dutyCharge?.type : shipmentType?.dutyCharge;
    return raw || 'DDU';
  }, [shipmentType?.dutyCharge]);

  const isDDP = currentDutyType === 'DDP';

  // Default dutyCharge to DDU for export shipments if not set
  useEffect(() => {
    if (isExport && setShipmentType && !shipmentType?.dutyCharge) {
      setShipmentType({
        ...shipmentType,
        dutyCharge: 'DDU',
      });
    }
  }, [isExport, shipmentType, setShipmentType]);

  // Add TODO-tagged console log as explicitly required
  // TODO: remove after verification
  console.info('[DutyTax] export check', {
    isExportShipment: isExport,
    consigneeCountry: consignee?.country,
    incoterm: shipmentType?.incoterm || extraDetails?.incoterm || 'FOB',
    selectedServiceId: selectedCSP?._courierId || selectedCSP?.serviceName,
    shouldShowDutyTaxSection: isExport,
  });

  const currencyLabel = shipmentType?.currency || defaultOrgCountryCurrency || 'INR';

  // Compute customs value: shipment value + freight + insurance
  const freightVal = Number(
    shipmentType?.freight !== undefined && shipmentType?.freight !== ''
      ? shipmentType.freight
      : extraDetails?.freight || 0,
  );
  const insuranceVal = Number(
    shipmentType?.freightInsurance !== undefined && shipmentType?.freightInsurance !== ''
      ? shipmentType.freightInsurance
      : extraDetails?.freightInsurance || 0,
  );
  const customsValue = Number(totalShipmentValue || 0) + freightVal + insuranceVal;

  // Selected or first service duty charge
  const effectiveDuty = useMemo(() => {
    if (selectedCSP) {
      return getDutyCharge(selectedCSP.pricing);
    }
    if (sortedCharges.length > 0) {
      return getDutyCharge(sortedCharges[0].pricing);
    }
    return 0;
  }, [selectedCSP, sortedCharges]);

  // EU Destination & EUR exchange rate
  const isEUDestination = isEUCountry(consignee?.country);
  const { rate: eurRate } = useEurExchangeRate({
    currency: currencyLabel,
    isEU: isEUDestination,
  });

  const eurEquivalentText = useMemo(() => {
    if (!isEUDestination || currencyLabel.toUpperCase() === 'EUR') return null;
    if (eurRate && eurRate > 0) {
      const eurVal = (Number(totalShipmentValue || 0) * eurRate).toFixed(2);
      return `(≈ €${eurVal})`;
    }
    return null;
  }, [isEUDestination, currencyLabel, eurRate, totalShipmentValue]);

  const handleDutyChargeChange = (type: 'DDU' | 'DDP') => {
    if (setShipmentType && shipmentType) {
      setShipmentType({
        ...shipmentType,
        dutyCharge: type,
      });
    }
  };

  return (
    <Grid container item sx={{ padding: { xs: '0 1.5rem', sm: '0 3rem' } }}>
      {/* ===================== DDP / DDU SECTION ===================== */}
      {isExport && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: '12px',
            border: `1.5px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.04)',
          }}
        >
          {/* Section Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptLongOutlinedIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'text.primary' }}>
                  Who pays duty & taxes? (DDP / DDU)
                </Typography>
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary', mt: 0.25 }}>
                Specify how import customs duties and destination taxes should be billed
              </Typography>
            </Box>
          </Box>

          {/* Computed Values Summary */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  bgcolor: alpha(theme.palette.background.default, 0.6),
                  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                  Customs Value (Shipment + Freight + Insurance)
                </Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary' }}>
                  {currencyLabel} {customsValue.toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  bgcolor: alpha(theme.palette.background.default, 0.6),
                  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary' }}>
                    Duty & Taxes Value {selectedCSP ? `(${selectedCSP?.serviceName})` : ''}
                  </Typography>
                  {selectedCSP?.accuracy && (
                    <Chip
                      label={`Accuracy: ${selectedCSP.accuracy}`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: 'info.main',
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: effectiveDuty > 0 ? '18px' : '14px',
                    fontWeight: effectiveDuty > 0 ? 700 : 500,
                    color: effectiveDuty > 0 ? 'primary.main' : 'text.secondary',
                    fontStyle: effectiveDuty > 0 ? 'normal' : 'italic',
                  }}
                >
                  {effectiveDuty > 0
                    ? `${currencyLabel} ${effectiveDuty.toFixed(2)}`
                    : 'Select a service below to view duty & taxes'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Segmented DDP / DDU Selector */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1 }}>
              Choose Billing Term *
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                p: 0.5,
                bgcolor: alpha(theme.palette.divider, 0.08),
                borderRadius: '10px',
                gap: 1,
              }}
            >
              <SbButton
                variant={currentDutyType === 'DDU' ? 'contained' : 'text'}
                onClick={() => handleDutyChargeChange('DDU')}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '8px',
                  fontWeight: currentDutyType === 'DDU' ? 700 : 500,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  bgcolor: currentDutyType === 'DDU' ? 'primary.main' : 'transparent',
                  color: currentDutyType === 'DDU' ? 'common.white' : 'text.primary',
                  boxShadow: currentDutyType === 'DDU' ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                  '&:hover': {
                    bgcolor: currentDutyType === 'DDU' ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                  },
                }}
              >
                DDU (Delivered Duty Unpaid)
              </SbButton>
              <SbButton
                variant={currentDutyType === 'DDP' ? 'contained' : 'text'}
                onClick={() => handleDutyChargeChange('DDP')}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '8px',
                  fontWeight: currentDutyType === 'DDP' ? 700 : 500,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  bgcolor: currentDutyType === 'DDP' ? 'primary.main' : 'transparent',
                  color: currentDutyType === 'DDP' ? 'common.white' : 'text.primary',
                  boxShadow: currentDutyType === 'DDP' ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                  '&:hover': {
                    bgcolor: currentDutyType === 'DDP' ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                  },
                }}
              >
                DDP (Delivered Duty Paid)
              </SbButton>
            </Box>

            {/* Helper text under selector */}
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={currentDutyType === 'DDP' ? 'Added to your charges' : 'Consignee pays on delivery'}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  bgcolor:
                    currentDutyType === 'DDP'
                      ? alpha(theme.palette.primary.main, 0.12)
                      : alpha(theme.palette.success.main, 0.12),
                  color: currentDutyType === 'DDP' ? 'primary.main' : 'success.dark',
                }}
              />
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {currentDutyType === 'DDP'
                  ? 'Import duty & taxes are added to your invoice and prepaid. Consignee pays nothing upon delivery.'
                  : 'Import duty & taxes will be collected directly from the consignee upon delivery.'}
              </Typography>
            </Box>
          </Box>

          {/* EU-Only Info Note */}
          {isEUDestination && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 20, mt: 0.25 }} />
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'info.dark', mb: 0.5 }}>
                    Goods value {currencyLabel} {Number(totalShipmentValue || 0).toFixed(2)}
                    {eurEquivalentText ? ` ${eurEquivalentText}` : ''} · €3 duty per line
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'info.dark', mb: 0.5 }}>
                    Final duty may differ
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.6 }}>
                    • Item 2: HS code has 6 digits, so the rate was matched at a broader level
                    <br />
                    • EU flat €3 per line applied: a fixed amount
                    <br />• Final duty and taxes are set by destination customs.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </Paper>
      )}

      {/* ===================== SERVICES AVAILABLE ===================== */}
      <Grid container className="mb-1" alignItems="center">
        <Grid item xs={6}>
          <Typography className="org-text">Services Available</Typography>
        </Grid>
        <Grid container item xs={6} justifyContent="end">
          <ChargeableWeight>{`Chargeable Weight: ${CSPChargesData?.chargeableWeight}`}</ChargeableWeight>
        </Grid>
      </Grid>
      <Grid container>
        {!isMobile && (
          <TableHeader container alignItems={'center'} item xs={12}>
            <Grid item className="sm-text" xs={1}></Grid>
            <Grid item className="sm-text" xs={4} sm={4} md={3.5} lg={4}>
              Carrier
            </Grid>
            <Grid item className="sm-text text-center" xs={2} sm={2} md={2} lg={1.5}>
              Chargeable Wt
            </Grid>
            {isExport && (
              <Grid item className="sm-text text-center" xs={2} sm={2} md={2} lg={2}>
                Duty & Charges
              </Grid>
            )}
            <Grid item className="sm-text" xs={2} lg={1.5} textAlign={'center'}>
              Cost
            </Grid>
            <Grid item className="sm-text" xs={2}></Grid>
          </TableHeader>
        )}
        {sortedCharges?.map((charge, ind) =>
          isMobile ? (
            <SingleCSPMobile
              key={ind}
              CSPCharge={charge}
              handleBookCSP={handleBookCSP}
              isDDP={isDDP}
              isExport={isExport}
            />
          ) : (
            <SingleCSP key={ind} CSPCharge={charge} handleBookCSP={handleBookCSP} isDDP={isDDP} isExport={isExport} />
          ),
        )}
      </Grid>
      <Actions setShowRates={setShowRates} />
    </Grid>
  );
}

export default CSPList;
